import axios, { AxiosError, AxiosHeaders, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError, LoginResponse } from '@/types/api';

export const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 20000,
});

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

/**
 * Modo suporte (aberto pelo HAOps). O token fica no sessionStorage, preso a
 * esta aba: no localStorage ele sobrescreveria a conta de quem dá suporte, e
 * ao vencer o refresh usaria o cookie DELE — trocando de conta sem avisar.
 */
export const CHAVE_SUPORTE = 'ha_suporte';

export type InfoSuporte = {
  sessao_id: string;
  operador_email: string;
  operador_nome?: string | null;
  motivo?: string;
  expira_em: string;
  aluno_email: string;
};

export function lerSuporte(): { token: string; info: InfoSuporte } | null {
  try {
    const bruto = sessionStorage.getItem(CHAVE_SUPORTE);
    return bruto ? (JSON.parse(bruto) as { token: string; info: InfoSuporte }) : null;
  } catch {
    return null;
  }
}

export function emSuporte(): boolean {
  return lerSuporte() !== null;
}

/** O token em uso: o de suporte, nesta aba, vence o login normal. */
export function tokenSalvo(): string | null {
  return lerSuporte()?.token ?? localStorage.getItem('ha_access');
}

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function registerUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

type Retryable = InternalAxiosRequestConfig & { _retry?: boolean; _skipRefresh?: boolean };

let refreshPromise: Promise<LoginResponse | null> | null = null;

async function doRefresh(): Promise<LoginResponse | null> {
  if (!refreshPromise) {
    refreshPromise = api
      .post<LoginResponse>('/auth/refresh', {}, { _skipRefresh: true } as Retryable)
      .then((r) => {
        accessToken = r.data.accessToken;
        return r.data;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<{ error?: ApiError }>) => {
    const original = error.config as Retryable | undefined;
    if (!original || original._retry || original._skipRefresh) throw error;

    // Sessão de suporte não renova: venceu ou foi encerrada, acabou.
    if (error.response?.status === 401 && emSuporte()) {
      onUnauthorized?.();
      throw error;
    }

    if (error.response?.status === 401) {
      original._retry = true;
      const data = await doRefresh();
      if (data) {
        const h = new AxiosHeaders(original.headers);
        h.set('Authorization', `Bearer ${data.accessToken}`);
        original.headers = h;
        return api(original);
      }
      onUnauthorized?.();
    }
    throw error;
  },
);

export function extractError(err: unknown): ApiError {
  if (err && typeof err === 'object' && 'isAxiosError' in err) {
    const ax = err as AxiosError<{ error?: ApiError }>;
    return ax.response?.data?.error ?? { code: 'UNKNOWN', message: ax.message };
  }
  return { code: 'UNKNOWN', message: String(err) };
}
